import React from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { getIn, useFormikContext } from 'formik';
import { noCreditStates } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';

import useSession from 'core/hooks/use-session';
import { Label } from 'core/components/label';
import InsuranceScoreOverride from '../insurance-score-override/insurance-score-override';

/**
 * Whether a person card shows the insurance score at all:
 *
 * - a brand can hide it from every user through `hiddenFields` (Forreston does today, because its
 *   farm rater does not rate on credit);
 * - external agency users never see it on a brand with `featureFlags.hideInsuranceScoreFromAgents`
 *   — the mutuals want the score in front of their own staff only (#35046). Forreston carries the
 *   flag as well, so the score stays away from its agents whenever it drops the `hiddenFields`
 *   entry above. Internal (non-agency) users are unaffected;
 * - only the applicant and co-applicant are scored, and there has to be a score — or an
 *   underwriter override in flight, which is how a score gets onto a person who was never
 *   scored (#34377);
 * - a non-Individual/Joint entity (Business, Corporation, LLC, Trust, Estate, …) is never
 *   credit-scored (see below).
 *
 * On a CFM farm/home/rental offer owned by a non-Individual/Joint entity the rater skips the
 * credit pull entirely — Clarion Door rates it at factor 1.00 — and leaves a `defaulted`
 * placeholder score (350) behind. That fabricated 350 is not a real score, so underwriters must
 * not see it in the Applicants section (#35532). The scope here is the *entity type*, not the
 * `defaulted` flag: the rater also defaults the score for no-credit states (which still show an
 * "N/A" indicator) and for pre-pull credit-avoidance deferrals, and those must keep rendering.
 * The Individual/Joint test mirrors the rater's own `#shouldSkipCreditForEntityType` and the FE's
 * existing entity checks in use-discounts / farm-discounts.
 */
export const isInsuranceScoreVisible = ({
  person,
  isOverriding,
  isAgency,
  hiddenFields,
  hideFromAgents,
  entityType
}) => {
  if ((hiddenFields ?? []).includes('applicant.insuranceScore')) {
    return false;
  }
  if (isAgency && hideFromAgents) {
    return false;
  }
  if (!person?.isPrimary && !person?.isCoApplicant) {
    return false;
  }
  const isNonIndividualEntity = !!entityType && entityType !== 'Individual' && entityType !== 'Joint';
  if (isNonIndividualEntity && !isOverriding) {
    return false;
  }
  return Boolean(person?.insuranceScore || isOverriding);
};

/**
 * The "Insurance Score" footer on a person card. Three cards show it — the offer People tab
 * applicant, the policy-details applicant, and the auto driver card — and they each rendered
 * their own copy of the same markup and the same visibility rules, so the rules live here once.
 *
 * A card that shows the score somewhere new must render this component; anything else silently
 * skips the rules above.
 */
const InsuranceScoreFooter = ({ className, fieldName, person, weighting, state }) => {
  const { isAgency } = useSession();
  const { values } = useFormikContext();

  const isOverriding = !!getIn(values, `${fieldName}.insuranceScoreOverride`);

  // A farm offer stores its entity on the form root (`values.entity`), same path the offer and
  // policy entity fields and the farm-discount rules read.
  const entityType = values?.entity?.type;

  if (
    !isInsuranceScoreVisible({
      person,
      isOverriding,
      isAgency,
      hiddenFields: appConfig?.hiddenFields,
      hideFromAgents: !!appConfig?.featureFlags?.hideInsuranceScoreFromAgents,
      entityType
    })
  ) {
    return null;
  }

  // The property cards show the home-weighted score and the driver card the auto-weighted one
  // (#32857); records written before the split carry a single `total`.
  // A no-hit (`homeNoScore` / `autoNoScore`) is stored with a placeholder in a rated band (CFM 684 /
  // 600) that it is never rated on, so it shows as "No score" rather than that number. #36824
  const isNoScore = !!person.insuranceScore?.[`${weighting}NoScore`];
  const pulledScore = isNoScore
    ? null
    : Number(person.insuranceScore?.[`${weighting}Total`]?.toFixed(1) || person.insuranceScore?.total?.toFixed(1));

  return (
    <Grid container alignItems="center" justifyContent="flex-end" className={className}>
      <Grid container item xs={5} direction="column" alignItems="flex-end">
        <Label type="infoLabel">Insurance Score</Label>
        <InsuranceScoreOverride
          fieldName={fieldName}
          pulledScore={pulledScore}
          isNA={!!noCreditStates[state]}
          isNoScore={isNoScore}
        />
      </Grid>
    </Grid>
  );
};

InsuranceScoreFooter.propTypes = {
  className: PropTypes.string,
  fieldName: PropTypes.string.isRequired,
  person: PropTypes.shape({
    isPrimary: PropTypes.bool,
    isCoApplicant: PropTypes.bool,
    insuranceScore: PropTypes.object
  }).isRequired,
  weighting: PropTypes.oneOf(['home', 'auto']).isRequired,
  state: PropTypes.string
};

InsuranceScoreFooter.defaultProps = {
  className: undefined,
  state: undefined
};

export default InsuranceScoreFooter;
